<template lang="pug">
aiq-dialog(:model-value="true"
           width="500"
           title="Recurrence"
           append-to-body
           :close-on-click-modal="true"
           :close-on-press-escape="true"
           :before-close="handleBeforeClose")
  aiq-form(label-width="130px"
                :hide-required-asterisk="true"
                class="has-default-form-items")
    aiq-form-item(label="Repeat")
      aiq-select(v-model="config.freq"
              :suffix-icon="Icon.CaretBottom"
              size="small"
              @change="onFreqChange")
        aiq-option(v-for="recurrence in recurrenceMenu"
                    :key="recurrence.value"
                    :label="recurrence.label"
                    :value="recurrence.value")

    aiq-form-item(label="Every")
        // Every X days
        // Every X weeks
        // Every X momnths

        aiq-col(:span="10")
          aiq-input-number(v-model="config.interval" :min="1" :max="100" type="number" size="small")
        aiq-col(:span="8")
          span(v-if="config.freq === RRule.DAILY") day(s)
          span(v-else-if="config.freq === RRule.WEEKLY") week(s)
          span(v-else-if="config.freq === RRule.MONTHLY") month(s)
          span(v-else-if="config.freq === RRule.YEARLY") year(s)

    aiq-form-item(v-if="config.freq !== RRule.DAILY" label="On")
        // No daily
        // weekly - selection from monday to friday
        // momnthly - X date or Xth week of specific day
        // yearly -
        .weekly-condition(v-if="config.freq === RRule.WEEKLY")
          aiq-select(v-model="config.byweekday"
                  multiple
                  size="small"
                  :suffix-icon="Icon.CaretBottom")
            aiq-option(v-for="day in daysSelect"
                        :key="day.value"
                        :label="day.label"
                        :value="day.value")

        .monthly-condition(v-if="config.freq === RRule.MONTHLY")
          aiq-col(:span="12")
            aiq-select(v-model="config.bymonthday"
                    multiple
                    size="small"
                    :suffix-icon="Icon.CaretBottom")
              aiq-option(v-for="monthday in monthdaySelect"
                        :key="monthday.value"
                        :label="monthday.label"
                        :value="monthday.value")

          aiq-col(:span="2") &nbsp;
          aiq-col(:span="10")
            span day

        .yearly-condition(v-if="config.freq === RRule.YEARLY")
          // Month & day selection
          aiq-select(v-model="config.bymonth"
                    multiple
                    size="small"
                    :suffix-icon="Icon.CaretBottom")
            aiq-option(v-for="month in monthSelect"
                       :key="month.value"
                       :label="month.label"
                       :value="month.value")

          aiq-select(v-model="config.bymonthday"
                      multiple
                      size="small"
                      :suffix-icon="Icon.CaretBottom")
            aiq-option(v-for="monthday in monthdaySelect"
                       :key="monthday.value"
                       :label="monthday.label"
                       :value="monthday.value")


    aiq-form-item
      template(#label)
        | Ends On
        span.optional-text (optional)
      aiq-date-picker(v-model="config.until"
                  type="date"
                  size="small"
                  popper-class="date-picker-popup"
                  :clearable="false"
                  placeholder="Select Date"
                  @change="(ev) => updateTimeValue('until', ev)"
                  :disabled-date="disabledDateForUntil"
                  format="YYYY/MM/DD")
    aiq-form-item(label="Description")
      span {{ recurrenceToText }}

  template(#footer)
    aiq-button(@click="onCancel") Cancel
    aiq-button(type="primary" @click="onSave") Save
</template>

<script>
import merge from 'lodash/merge';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import startCase from 'lodash/startCase';
import omitBy from 'lodash/omitBy';
import omit from 'lodash/omit';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import isEqual from 'lodash/isEqual';
import { RRule } from 'rrule';
import dayjs from '@/localPlugins/dayjs';
import { confirmBeforeDialogClose } from '@/libs';

export default {
  components: {
  },
  props: {
    modelValue: {
      type: Object,
      default: null,
    },
  },
  emits: ['cancel', 'submit', 'update:freq'],
  computed: {
    RRule() {
      return RRule;
    },
    recurrenceMenu() {
      return [
        {
          value: RRule.DAILY,
          label: 'Daily',
        },
        {
          value: RRule.WEEKLY,
          label: 'Weekly',
        },
        {
          value: RRule.MONTHLY,
          label: 'Monthly',
        },
        {
          value: RRule.YEARLY,
          label: 'Yearly',
        },
      ];
    },
    daysSelect() {
      return [
        {
          value: RRule.MO.weekday,
          label: 'Monday',
        },
        {
          value: RRule.TU.weekday,
          label: 'Tuesday',
        },
        {
          value: RRule.WE.weekday,
          label: 'Wednesday',
        },
        {
          value: RRule.TH.weekday,
          label: 'Thursday',
        },
        {
          value: RRule.FR.weekday,
          label: 'Friday',
        },
        {
          value: RRule.SA.weekday,
          label: 'Saturday',
        },
        {
          value: RRule.SU.weekday,
          label: 'Sunday',
        },
      ];
    },

    monthSelect() {
      // return the below format
      // [
      //   {
      //      value: 1,
      //      label: 'Jan',
      //   }
      //   ...
      // ]
      return [
        'Jan', 'Feb', 'Mar', 'Apr', 'May',
        'Jun', 'Jul', 'Aug', 'Sep', 'Oct',
        'Nov', 'Dec',
      ].map((label, value) => ({ label, value: value + 1 }));
    },

    monthdaySelect() {
      // 1 ~ 31
      return Array.from({ length: 31 }, (_, i) => i + 1)
        .map(value => ({ label: value, value }));
    },
    recurrenceToText() {
      const localTimeOffset = new Date().getTimezoneOffset();
      const config = cloneDeep(this.cleanConfig);
      config.dtstart = dayjs(config.dtstart).add(-localTimeOffset, 'm').toDate();
      if (config.until) {
        config.until = dayjs(config.until).add(-localTimeOffset, 'm').toDate();
      }
      return startCase(new RRule(config).toText());
    },
    cleanConfig() {
      return omitBy(omit(this.config, ['id']), isNil);
    },
    isEdited() {
      return !isEqual(this.cleanConfig, this.originalConfig);
    },
  },
  methods: {
    onFreqChange(newFreq) {
      this.$emit('update:freq', newFreq);
    },
    onSave() {
      // Validate required "On" field based on frequency
      if (this.config.freq !== RRule.DAILY) { // Not daily
        if (this.config.freq === RRule.WEEKLY) {
          if (isEmpty(this.config.byweekday) || this.config.byweekday.length === 0) {
            this.$aiq.notify.error('Please select at least one day for weekly recurrence');
            return;
          }
        } else if (this.config.freq === RRule.MONTHLY) {
          if (isEmpty(this.config.bymonthday) || this.config.bymonthday.length === 0) {
            this.$aiq.notify.error('Please select at least one day for monthly recurrence');
            return;
          }
        } else if (this.config.freq === RRule.YEARLY) {
          if (isEmpty(this.config.bymonth) || this.config.bymonth.length === 0) {
            this.$aiq.notify.error('Please select at least one month for yearly recurrence');
            return;
          }
          if (isEmpty(this.config.bymonthday) || this.config.bymonthday.length === 0) {
            this.$aiq.notify.error('Please select at least one day for yearly recurrence');
            return;
          }
        }
      }

      // Clean up null fields before emitting submit event
      return this.$emit('submit', omitBy(this.config, isNil));
    },
    handleBeforeClose(done) {
      if (!this.isEdited) {
        this.emitCancel();
        done();
        return;
      }

      confirmBeforeDialogClose(
        this,
        () => {
          this.emitCancel();
          done();
        },
        () => {
          // User cancelled - keep the dialog open (don't call done)
        },
      );
    },
    onCancel() {
      if (!this.isEdited) {
        this.emitCancel();
        return;
      }

      confirmBeforeDialogClose(this, () => {
        this.emitCancel();
      });
    },
    emitCancel() {
      return this.$emit('cancel');
    },
    updateTimeValue(field, val) {
      // Date picker returns a date with 00:00:00. The time should be
      // corrected with modelValue.dtstart time
      const timeSource = dayjs(this.modelValue.dtstart);
      set(this.config, field, dayjs(val).set({
        hour: timeSource.hour(),
        minute: timeSource.minute(),
        second: timeSource.second(),
        millisecond: timeSource.millisecond(),
      }).toISOString());
    },
    disabledDateForDtstart(time) {
      // Should refer to original dtstart of props.
      return time.getTime() < dayjs(this.modelValue.dtstart).valueOf();
    },
    disabledDateForUntil(time) {
      // Should refer to changed dtstart of data.
      return time.getTime() < dayjs(this.config.dtstart).valueOf();
    },
  },
  data() {
    const initialConfig = merge({
      dtstart: null,
      freq: RRule.DAILY,
      until: null,
      count: null,
      interval: 1,
      byweekday: [],
      bymonthday: [],
      byyearday: [],
      tzid: '',
    }, this.modelValue);

    return {
      config: initialConfig,
      originalConfig: cloneDeep(omitBy(omit(initialConfig, ['id']), isNil)),
    };
  },
};
</script>

<style lang="scss" scoped>
.weekly-condition, .monthly-condition {
  width: 100%;
}
</style>
